import { useCallback, useState } from "react"
import { maskPiiInDom } from "@/lib/support/pii-mask"

const HIDE_ATTR = "data-screenshot-hidden"
const HIDE_STYLE_ID = "screenshot-hide-style"

/**
 * Captura uma screenshot do DOM atual usando html-to-image.
 *
 * Pipeline:
 *   1. Aplica máscara PII (CPF, CNPJ, telefone, email)
 *   2. Esconde elementos com [data-screenshot-hidden] (FAB + widget overlay)
 *   3. Renderiza com html-to-image.toJpeg (quality 0.7)
 *   4. Se > 200KB, redraw em canvas menor (downscale 0.75x) até caber
 *   5. Restaura PII + remove style de hide
 *
 * Limitação conhecida: backdrop-filter (glass morphism) é flat na imagem —
 * é uma limitação fundamental SVG/canvas, não tem fix.
 */

export function useScreenshot() {
  const [isCapturing, setIsCapturing] = useState(false)

  const capture = useCallback(async (): Promise<Blob | null> => {
    if (typeof document === "undefined") return null

    setIsCapturing(true)
    let restorePii: (() => void) | null = null

    // Esconde apenas elementos marcados com [data-screenshot-hidden].
    // Outros modais/drawers (LeadDrawer, etc) continuam visíveis — usuário
    // pode estar reportando bug sobre o que está vendo dentro deles.
    const style = document.createElement("style")
    style.id = HIDE_STYLE_ID
    style.textContent = `[${HIDE_ATTR}="true"] { visibility: hidden !important; }`
    document.head.appendChild(style)

    try {
      // Mascarar PII
      restorePii = maskPiiInDom()

      // Lazy load html-to-image (~50KB) só quando captura é solicitada
      const htmlToImage = await import("html-to-image")

      const node = document.body
      const targetWidth = Math.min(window.innerWidth, 1600)

      // #192: UMA passada só, sem `cacheBust` e sem loop de downscale. Antes,
      // `cacheBust: true` forçava re-download de TODAS as imagens da página
      // (avatares, logos) e o loop re-serializava a página inteira até 4x —
      // numa tela pesada (Funil) isso levava ~40s e travava a abertura. Sem
      // cacheBust o html-to-image inlina as imagens do cache (rápido, e não
      // suja o canvas). `pixelRatio` limita a largura final (~1280px) pra
      // segurar o tamanho do JPEG numa passada. `skipFonts` (task #25) evita
      // erros de console com @font-face cross-origin.
      const pixelRatio = Math.min(1, 1280 / targetWidth)
      const dataUrl = await htmlToImage.toJpeg(node, {
        width: targetWidth,
        height: window.innerHeight,
        skipFonts: true,
        backgroundColor: getComputedStyle(document.body).backgroundColor || "#f0f4ff",
        quality: 0.6,
        pixelRatio,
      })

      return dataUrlToBlob(dataUrl)
    } catch (err) {
      console.error("Screenshot capture failed:", err)
      return null
    } finally {
      restorePii?.()
      const styleEl = document.getElementById(HIDE_STYLE_ID)
      styleEl?.remove()
      setIsCapturing(false)
    }
  }, [])

  return { capture, isCapturing }
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, data] = dataUrl.split(",")
  const mime = header.match(/data:([^;]+)/)?.[1] ?? "image/jpeg"
  const binary = atob(data)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new Blob([bytes], { type: mime })
}
